/**
 * Event Partner API Service
 */

import { API_URL } from "@/config/api";
import type {
  CommunityEventsResponse,
  EventTripsResponse,
  EventTripsFilters,
  EventPartnerTrip,
  TripStatistics,
  VehicleTypeBreakdown,
  TripState,
} from "@/types/eventPartner";

const EVENT_PARTNER_TOKEN_KEY = "eventPartnerToken";
const EVENT_PARTNER_USER_KEY = "eventPartnerUser";

/**
 * Get the stored event partner token
 */
export const getEventPartnerToken = (): string | null => {
  return localStorage.getItem(EVENT_PARTNER_TOKEN_KEY);
};

/**
 * Check if event partner is authenticated
 */
export const isEventPartnerAuthenticated = (): boolean => {
  return !!getEventPartnerToken();
};

/**
 * Login as an event partner
 */
export const eventPartnerLogin = async (
  username: string,
  password: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(`${API_URL}disco-auth/login/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      if (response.status === 400 || response.status === 401) {
        return { success: false, error: "Invalid username or password" };
      }
      return { success: false, error: "Login failed. Please try again." };
    }

    const data = await response.json();
    
    // Store the token
    localStorage.setItem(EVENT_PARTNER_TOKEN_KEY, data.token);
    
    // Store basic user info
    localStorage.setItem(
      EVENT_PARTNER_USER_KEY,
      JSON.stringify({ username })
    );

    return { success: true };
  } catch (error) {
    console.error("Event partner login error:", error);
    return { success: false, error: "Network error. Please check your connection." };
  }
};

/**
 * Logout the event partner
 */
export const eventPartnerLogout = (): void => {
  localStorage.removeItem(EVENT_PARTNER_TOKEN_KEY);
  localStorage.removeItem(EVENT_PARTNER_USER_KEY);
};

/**
 * Get the stored event partner user info
 */
export const getEventPartnerUser = (): { username: string } | null => {
  const userStr = localStorage.getItem(EVENT_PARTNER_USER_KEY);
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
};

/**
 * Fetch events managed by the logged-in partner
 */
export const getManagedEvents = async (): Promise<CommunityEventsResponse> => {
  const token = getEventPartnerToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  // Use managed_by_me=true filter to only get events where the user is a manager
  const response = await fetch(`${API_URL}communityevents/?managed_by_me=true`, {
    method: "GET",
    headers: {
      Authorization: `Token ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (response.status === 401) {
    eventPartnerLogout();
    throw new Error("Session expired. Please login again.");
  }

  if (!response.ok) {
    throw new Error("Failed to fetch managed events");
  }

  return response.json();
};

/**
 * Fetch trips for a specific event
 */
export const getEventTrips = async (
  eventId: string,
  filters?: EventTripsFilters
): Promise<EventTripsResponse> => {
  const token = getEventPartnerToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const params = new URLSearchParams();
  if (filters?.trip_state) params.append("trip_state", filters.trip_state);
  if (filters?.search) params.append("search", filters.search);
  if (filters?.ordering) params.append("ordering", filters.ordering);
  if (filters?.page) params.append("page", filters.page.toString());
  if (filters?.page_size) params.append("page_size", filters.page_size.toString());

  const queryString = params.toString();
  const url = `${API_URL}trips/event/${eventId}/${queryString ? `?${queryString}` : ""}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Token ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (response.status === 401) {
    eventPartnerLogout();
    throw new Error("Session expired. Please login again.");
  }

  if (response.status === 403) {
    throw new Error("You don't have permission to view this event's trips.");
  }

  if (response.status === 404) {
    throw new Error("Event not found.");
  }

  if (!response.ok) {
    throw new Error("Failed to fetch event trips");
  }

  return response.json();
};

/**
 * Fetch all trips for an event (handles pagination)
 */
export const getAllEventTrips = async (eventId: string): Promise<EventPartnerTrip[]> => {
  const allTrips: EventPartnerTrip[] = [];
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const response = await getEventTrips(eventId, { page, page_size: 100 });
    allTrips.push(...response.results);
    hasMore = response.next !== null;
    page++;
  }

  return allTrips;
};

/**
 * Calculate statistics from trips
 */
export const calculateTripStatistics = (trips: EventPartnerTrip[]): TripStatistics => {
  // Filter out UNCLAIMED trips - only include BOOKED or later in calculations
  const bookedOrLaterTrips = trips.filter((trip) => trip.trip_state !== "UNCLAIMED");
  
  const byVehicleTypeMap: Record<string, { count: number; revenue: number }> = {};
  const byTripState: Partial<Record<TripState, number>> = {};
  let totalRevenue = 0;

  bookedOrLaterTrips.forEach((trip) => {
    const price = parseFloat(trip.rider_price) || 0;
    totalRevenue += price;

    // Group by vehicle type
    const vehicleType = trip.user_friendly_name || "Unknown";
    if (!byVehicleTypeMap[vehicleType]) {
      byVehicleTypeMap[vehicleType] = { count: 0, revenue: 0 };
    }
    byVehicleTypeMap[vehicleType].count++;
    byVehicleTypeMap[vehicleType].revenue += price;

    // Group by status
    byTripState[trip.trip_state] = (byTripState[trip.trip_state] || 0) + 1;
  });

  // Convert vehicle type map to array
  const byVehicleType: VehicleTypeBreakdown[] = Object.entries(byVehicleTypeMap)
    .map(([vehicleType, data]) => ({
      vehicleType,
      count: data.count,
      revenue: data.revenue,
    }))
    .sort((a, b) => b.revenue - a.revenue);

  return {
    totalTrips: bookedOrLaterTrips.length,
    totalRevenue,
    byVehicleType,
    byTripState: byTripState as Record<TripState, number>,
  };
};
